"use client"

import { useState, useEffect, useCallback } from "react"
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  RadioGroup,
  Radio,
  FormControlLabel,
  Divider,
  CircularProgress,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
} from "@mui/material"
import { useNotification } from "../../contexts/NotificationContext"
import { getPlans } from "../../services/subscription.service"
import { changePlan } from "../../services/subscription.service"
// Import the error handler
import { handleApiError } from "../../utils/error-handler"
// Import Stripe
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js"
import { getStripe, isStripeConfigured } from "../../utils/stripe-helper"

// Initialize Stripe
const stripePromise = getStripe();
const stripeIsConfigured = isStripeConfigured();

// Show warning if Stripe key is missing
if (!stripeIsConfigured) {
  console.warn("Stripe could not be initialized. Please check your Stripe configuration.");
}

// Payment form component
const PaymentForm = ({ clientSecret, onSuccess, onError }) => {
  const stripe = useStripe()
  const elements = useElements()
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!stripe || !elements) {
      return
    }
    
    setProcessing(true)
    
    const { error } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/subscription/success`,
      },
    })
    
    if (error) {
      setError(error.message)
      onError(error.message)
    } else {
      onSuccess()
    }
    
    setProcessing(false)
  }
  
  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement />
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Button 
          type="submit" 
          variant="contained" 
          disabled={!stripe || processing}
        >
          {processing ? "Processing..." : "Pay Now"}
        </Button>
      </Box>
    </form>
  )
}

const SubscriptionChangePlan = ({ open, onClose, subscription, onPlanChanged, organizationId }) => {
  const { showNotification } = useNotification()
  const [plans, setPlans] = useState([])
  const [selectedPlanId, setSelectedPlanId] = useState("")
  const [selectedBillingCycle, setSelectedBillingCycle] = useState("")
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState("")
  const [proratedAmount, setProratedAmount] = useState(null)
  const [changeType, setChangeType] = useState(null)
  const [paymentIntent, setPaymentIntent] = useState(null)
  const [showPaymentForm, setShowPaymentForm] = useState(false)
  const [masterPassword, setMasterPassword] = useState("")
  const [requireMasterPassword, setRequireMasterPassword] = useState(false)

  const fetchPlans = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getPlans({ organizationId })
      const validPlans = response.data.filter((plan) => plan && plan.isActive && plan.name)
      setPlans(validPlans)
      if (subscription && subscription.planId) {
        setSelectedPlanId(subscription.planId._id)
      }
    } catch (error) {
      console.error("Failed to fetch plans:", error)
      setError("Failed to load available plans")
    } finally {
      setLoading(false)
    }
  }, [organizationId, subscription])

  useEffect(() => {
    if (open) {
      fetchPlans()
      setSelectedBillingCycle(subscription?.billingCycle || "monthly")
      setMasterPassword("")
    }
  }, [open, organizationId, subscription?.billingCycle, fetchPlans])

  useEffect(() => {
    if (subscription && plans.length > 0) {
      // Find the current plan price
      const currentPlan = plans.find((plan) => plan && plan._id === subscription.planId._id)

      if (selectedPlanId && currentPlan) {
        const selectedPlan = plans.find((plan) => plan && plan._id === selectedPlanId)

        if (selectedPlan) {
          // If either the current or new context involves a lifetime plan/cycle,
          // skip monthly/annual-style proration and let the backend handle it.
          const isLifetimeContext =
            subscription.billingCycle === "lifetime" ||
            selectedBillingCycle === "lifetime" ||
            selectedPlan.planType === "lifetime"

          if (isLifetimeContext) {
            const lifetimePrice = selectedPlan.lifetimePrice || 0
            setChangeType(lifetimePrice > 0 ? "upgrade" : "same")
            setProratedAmount(null)
            return
          }

          // Get prices based on selected billing cycle
          const currentPrice = subscription.billingCycle === "annual" ? currentPlan.annualPrice : currentPlan.monthlyPrice
          const newPrice = selectedBillingCycle === "annual" ? selectedPlan.annualPrice : selectedPlan.monthlyPrice

          // Calculate the daily rates based on respective billing cycles
          const currentDaysInCycle = subscription.billingCycle === "annual" ? 365 : 30
          const newDaysInCycle = selectedBillingCycle === "annual" ? 365 : 30

          const currentDate = new Date()
          const periodStart = new Date(subscription.currentPeriodStart)

          // Calculate days remaining in current cycle
          const daysElapsed = Math.floor((currentDate - periodStart) / (1000 * 60 * 60 * 24))
          const daysRemaining = Math.max(0, currentDaysInCycle - daysElapsed)

          // Calculate daily rates for both plans using their respective cycles
          const currentPlanDailyRate = currentPrice / currentDaysInCycle
          const newPlanDailyRate = newPrice / newDaysInCycle

          // Calculate remaining value of current plan
          const currentPlanRemainingValue = currentPlanDailyRate * daysRemaining

          // Calculate cost of new plan for the remaining period
          const newPlanRemainingValue = newPlanDailyRate * daysRemaining

          const proratedDelta = newPlanRemainingValue - currentPlanRemainingValue

          if (proratedDelta > 0) {
            setChangeType("upgrade")
            setProratedAmount({
              amount: proratedDelta.toFixed(2),
              currency: subscription.currency || "USD",
              daysRemaining,
              currentPlanDailyRate: currentPlanDailyRate.toFixed(2),
              newPlanDailyRate: newPlanDailyRate.toFixed(2),
              currentPrice: currentPrice.toFixed(2),
              newPrice: newPrice.toFixed(2),
              currentCycle: subscription.billingCycle,
              newCycle: selectedBillingCycle,
              currentPlanRemainingValue: currentPlanRemainingValue.toFixed(2),
              newPlanRemainingValue: newPlanRemainingValue.toFixed(2)
            })
          } else if (proratedDelta < 0) {
            setChangeType("downgrade")
            setProratedAmount(null)
          } else {
            setChangeType("same")
            setProratedAmount(null)
          }
        }
      }
    }
  }, [selectedPlanId, selectedBillingCycle, subscription, plans])

  // fetchPlans moved into useCallback above

  // Determine selected plan and allowed billing cycles
  const selectedPlan = plans.find((plan) => plan && plan._id === selectedPlanId)
  const isLifetimePlan = selectedPlan?.planType === "lifetime"
  const RECURRING_CYCLES = ["monthly", "annual"]
  const allowedCycles = isLifetimePlan ? ["lifetime"] : RECURRING_CYCLES

  const handlePlanChange = (event) => {
    const selected = event.target.value
    setSelectedPlanId(selected)
    const plan = plans.find(p => p && p._id === selected)
    setRequireMasterPassword(!!(plan && plan.isPublic === false))
    setMasterPassword("")

    if (plan?.planType === "lifetime") {
      // Lifetime plans always use the lifetime cycle
      setSelectedBillingCycle("lifetime")
    } else {
      // For regular plans, prefer the current subscription's recurring cycle if valid, otherwise default to monthly
      const currentCycle = subscription?.billingCycle
      const defaultCycle = RECURRING_CYCLES.includes(currentCycle) ? currentCycle : "monthly"
      setSelectedBillingCycle(defaultCycle)
    }
  }

  const handleBillingCycleChange = (event, newBillingCycle) => {
    if (newBillingCycle !== null) {
      setSelectedBillingCycle(newBillingCycle)
    }
  }

  const handleSubmit = async () => {
    const selectedPlan = plans.find((plan) => plan && plan._id === selectedPlanId)

    if (!selectedPlan) {
      setError("Please select a plan")
      return
    }

    if (selectedPlan.isPublic === false) {
      if (!masterPassword) {
        setError("Master password is required for private plans")
        return
      }
    }

    // Ensure we never send an invalid billing cycle for the selected plan type
    const safeBillingCycle = allowedCycles.includes(selectedBillingCycle)
      ? selectedBillingCycle
      : allowedCycles[0]

    setSubmitting(true)
    setError("")

    try {
      // Pass both the plan ID and billing cycle
      const result = await changePlan(subscription._id, selectedPlan._id, safeBillingCycle, masterPassword)
      console.log("PLAN CHANGE RESULT:", result);

      // If result is null, it means we're redirecting to Stripe Checkout
      if (!result) {
        return
      }

      // If we have a payment intent, show the payment form
      if (result.requiresPayment) {
        setPaymentIntent(result.paymentIntent)
        setShowPaymentForm(true)
        setSubmitting(false)
        return
      }

      if (changeType === "upgrade") {
        showNotification("Plan upgraded successfully", "success")
      } else if (changeType === "downgrade") {
        showNotification("Plan downgrade scheduled for next billing cycle", "success")
      }

      if (onPlanChanged) {
        onPlanChanged(result.subscription)
      }

      onClose()
    } catch (error) {
      const errorMessage = handleApiError(error)
      setError(errorMessage)
      console.error("Plan change error:", errorMessage)
      showNotification("Failed to change plan", "error")
    } finally {
      setSubmitting(false)
    }
  }

  const handlePaymentSuccess = () => {
    if (changeType === "upgrade") {
      showNotification("Plan upgraded successfully", "success")
    } else if (changeType === "downgrade") {
      showNotification("Plan downgrade scheduled for next billing cycle", "success")
    }
    
    if (onPlanChanged && paymentIntent) {
      onPlanChanged(paymentIntent.subscription)
    }
    
    onClose()
  }

  const handlePaymentError = (errorMessage) => {
    setError(errorMessage)
    showNotification("Payment failed", "error")
  }

  const handleClose = () => {
    setMasterPassword("")
    setRequireMasterPassword(false)
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Change Subscription Plan</DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : showPaymentForm && paymentIntent ? (
          stripeIsConfigured ? (
            <Elements stripe={stripePromise} options={{ clientSecret: paymentIntent.client_secret }}>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Complete Payment
                </Typography>
                <Typography variant="body2" paragraph>
                  Your card will be charged {proratedAmount?.amount} {proratedAmount?.currency} for the plan upgrade.
                </Typography>
                <PaymentForm
                  clientSecret={paymentIntent.client_secret}
                  onSuccess={handlePaymentSuccess}
                  onError={handlePaymentError}
                />
              </Box>
            </Elements>
          ) : (
            <Alert severity="error" sx={{ mt: 2 }}>
              Stripe could not be initialized. Please check your Stripe configuration.
            </Alert>
          )
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Typography variant="subtitle1" gutterBottom>
              Current Plan: {subscription?.planId?.name || "Unknown"} ({subscription?.billingCycle})
            </Typography>

            <Box sx={{ mb: 3, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Billing Cycle
              </Typography>
              {isLifetimePlan ? (
                <Typography variant="body2">
                  Lifetime (one-time payment)
                </Typography>
              ) : (
                <ToggleButtonGroup
                  value={selectedBillingCycle}
                  exclusive
                  onChange={handleBillingCycleChange}
                  aria-label="billing cycle"
                  size="small"
                >
                  <ToggleButton value="monthly">Monthly</ToggleButton>
                  <ToggleButton value="annual">Annual</ToggleButton>
                </ToggleButtonGroup>
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a new plan below.{" "}
              {changeType === "upgrade" && proratedAmount ? (
                <>
                  Upgrades take effect immediately with prorated billing.
                  <Box sx={{ mt: 2, p: 2, bgcolor: "#f5f5f5", borderRadius: 1 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      How Proration Works:
                    </Typography>
                    <Typography variant="body2" component="div">
                      <strong>Current Plan Details:</strong>
                      <br />
                      • Plan: {subscription?.planId?.name}
                      <br />
                      • Billing Cycle:{" "}
                      {proratedAmount?.currentCycle === "annual"
                        ? "Annual (365 days)"
                        : proratedAmount?.currentCycle === "lifetime"
                        ? "Lifetime (one-time payment)"
                        : "Monthly (30 days)"}
                      <br />
                      • Price: ${proratedAmount?.currentPrice}/
                      {proratedAmount?.currentCycle === "annual"
                        ? "year"
                        : proratedAmount?.currentCycle === "lifetime"
                        ? "one-time"
                        : "month"}
                      <br />
                      • Daily Rate: ${proratedAmount?.currentPlanDailyRate}/day
                      <br /><br />
                      
                      <strong>New Plan Details:</strong>
                      <br />
                      • Plan: {plans.find(p => p._id === selectedPlanId)?.name}
                      <br />
                      • Billing Cycle:{" "}
                      {proratedAmount?.newCycle === "annual"
                        ? "Annual (365 days)"
                        : proratedAmount?.newCycle === "lifetime"
                        ? "Lifetime (one-time payment)"
                        : "Monthly (30 days)"}
                      <br />
                      • Price: ${proratedAmount?.newPrice}/
                      {proratedAmount?.newCycle === "annual"
                        ? "year"
                        : proratedAmount?.newCycle === "lifetime"
                        ? "one-time"
                        : "month"}
                      <br />
                      • Daily Rate: ${proratedAmount?.newPlanDailyRate}/day
                      <br /><br />
                      
                      <strong>Proration Calculation:</strong>
                      <br />
                      1. Days remaining in current cycle: {proratedAmount?.daysRemaining} days
                      <br />
                      2. Current plan remaining value: ${proratedAmount?.currentPlanRemainingValue}
                      <br />
                      3. New plan cost for remaining days: ${proratedAmount?.newPlanRemainingValue}
                      <br />
                      4. Prorated charge: ${proratedAmount?.amount} (difference between new plan cost and current plan remaining value)
                      <br /><br />
                      <em>Note: When switching between billing cycles, we calculate daily rates based on the respective cycle lengths (30 days for monthly, 365 days for annual) to ensure accurate proration.</em>
                    </Typography>
                  </Box>
                </>
              ) : changeType === "upgrade" ? (
                "Upgrades take effect immediately."
              ) : (
                "Downgrades will take effect at the end of your current billing cycle."
              )}
            </Typography>

            <RadioGroup value={selectedPlanId} onChange={handlePlanChange}>
              {plans
                .filter((plan) => plan && plan.name)
                .map((plan) => (
                  <Box key={plan._id} sx={{ mb: 2 }}>
                    <FormControlLabel
                      value={plan._id}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">{plan.name}</Typography>
                          <Typography variant="body2">
                            {selectedBillingCycle === "lifetime" || plan.planType === "lifetime" ? (
                              <>
                                {plan.lifetimePrice && plan.lifetimePrice > 0
                                  ? `$${plan.lifetimePrice} one-time`
                                  : "Included with license"}
                              </>
                            ) : selectedBillingCycle === "annual" ? (
                              <>
                                ${plan.annualPrice}/year{" "}
                                <Typography component="span" color="text.secondary" variant="body2">
                                  (${(plan.annualPrice / 12).toFixed(2)}/month, ${(plan.annualPrice / 365).toFixed(2)}/day)
                                </Typography>
                              </>
                            ) : (
                              <>
                                ${plan.monthlyPrice}/month{" "}
                                <Typography component="span" color="text.secondary" variant="body2">
                                  (${plan.monthlyPrice * 12}/year, ${(plan.monthlyPrice / 30).toFixed(2)}/day)
                                </Typography>
                              </>
                            )}
                          </Typography>
                        </Box>
                      }
                    />
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 4 }}>
                      {plan.description}
                    </Typography>
                    {selectedPlanId === plan._id && proratedAmount && changeType === "upgrade" && (
                      <Alert severity="info" sx={{ ml: 4, mt: 1, maxWidth: "fit-content" }}>
                        <Typography variant="body2">
                          <strong>Prorated Charge Details:</strong>
                          <br />
                          • Remaining days in current cycle: {proratedAmount.daysRemaining} days
                          <br />
                          • Additional amount due today: ${proratedAmount.amount} {proratedAmount.currency}
                          <br />
                          <em>This covers the price difference for the remaining period of your current billing cycle.</em>
                        </Typography>
                      </Alert>
                    )}
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                ))}
            </RadioGroup>
            {requireMasterPassword && (
              <Box sx={{ mt: 2 }}>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This is a private plan. Please enter the master password to proceed.
                </Alert>
                <TextField
                  fullWidth
                  type="password"
                  label="Master Password"
                  variant="outlined"
                  value={masterPassword}
                  onChange={(e) => setMasterPassword(e.target.value)}
                />
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        {!showPaymentForm && (
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={submitting || !selectedPlanId || selectedPlanId === subscription?.planId?._id}
          >
            {submitting ? "Changing Plan..." : "Change Plan"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}

export default SubscriptionChangePlan

